import { useState } from 'react';
import { Col, Row, Spin, Tabs, Radio, Tag, Table, Badge, Button, Switch, message, Popover } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { handlers } from '../../../../helpers/helpers';
import { signExpertBulk } from '../../../../services/exam';
import { useTranslation } from 'react-i18next';
import { capitalize } from '../../../../utils/stringUtils';
import moment from 'moment';
import { HasAccess } from '@permify/react-role';
import { useBoxContext } from '../../../../context/BoxContext';
import { callPatient } from '../../../../services/general';
import { getCookie } from '../../../../services/cookies';
import showOccupiedPatientModal from '../OccupiedPatientModal';
import showBlockedCallModal from '../BlockedCallModal';
import { EXAM_SPECIALTY_IDS } from '../../../../pages/MedicalConsultationPage/constants/examSpecialties';

const { TabPane } = Tabs;

const SignatureTabs = ({
  signatureServices,
  onOpenDocumentDrawer,
  onOpenDetailDrawer,
  onRefreshData,
  onFilterChange,
  expertStatusFilter,
  onChangeActiveTab,
}) => {
  const { t } = useTranslation();
  const { hasBoxes } = useBoxContext();
  const countryCode = getCookie('country_code');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [signingSelected, setSigningSelected] = useState(false);
  const [loadingPerRow, setLoadingPerRow] = useState({});
  const isSpecialtyUltrasound = (specialtyId) => specialtyId === EXAM_SPECIALTY_IDS[countryCode]?.ULTRASOUND;

  const handleCallPatient = async (admissionPatientId, admissionPatientExamId, forceOccupiedCall = false, forceRecentCall = false) => {
    const box = getCookie('box_id');
    if (!box || typeof box !== 'string' || box.trim() === '') {
      message.warning(t('examPage.examTabs.callPatientBoxWarning'));
      return;
    }

    setLoadingPerRow(prev => ({ ...prev, [admissionPatientExamId]: true }));

    try {
      await callPatient({ admissionPatientId: Number(admissionPatientId), box, forceOccupiedCall, forceRecentCall });
      message.success(t('examPage.examTabs.callPatientSuccess'));
    } catch (error) {
      const errorMessage = typeof error === 'string' ? error : error?.message || error?.toString?.() || 'Error';

      if (errorMessage?.includes('PATIENT_OCCUPIED_IN_EXAM') && !forceOccupiedCall) {
        setLoadingPerRow(prev => ({ ...prev, [admissionPatientExamId]: false }));
        showOccupiedPatientModal(errorMessage, () => handleCallPatient(admissionPatientId, admissionPatientExamId, true, false), t);
        return;
      }

      if (errorMessage?.includes('PATIENT_RECENTLY_CALLED') && !forceRecentCall) {
        setLoadingPerRow(prev => ({ ...prev, [admissionPatientExamId]: false }));
        showBlockedCallModal(errorMessage, () => handleCallPatient(admissionPatientId, admissionPatientExamId, forceOccupiedCall, true), t);
        return;
      }

      message.error(errorMessage || t('examPage.examTabs.callPatientError'));
      console.error('Error handleCallPatient:', error);
    } finally {
      setLoadingPerRow(prev => ({ ...prev, [admissionPatientExamId]: false }));
    }
  };

  const loadMore = (event, examType) => {
    const direction = event.target.value;
    const service = signatureServices[examType];
    service.setCurrentPage(direction === 'next' ? service.currentPage + 1 : service.currentPage - 1);
  };

  const handleToggleClassification = (checked) => {
    if (onFilterChange) onFilterChange(checked ? 'pending' : 'normal');
  };

  const handleSignSelected = async () => {
    if (selectedRowKeys.length === 0) return;
    try {
      setSigningSelected(true);
      const response = await signExpertBulk(selectedRowKeys);
      if (response.signedCount > 0) {
        message.success({
          content: `${response.signedCount} examen(es) firmado(s) correctamente`,
          className: 'signature-message',
        });
      }
      if (response.failedCount > 0) {
        message.warning({
          content: `${response.failedCount} examen(es) no pudieron ser firmados`,
          className: 'signature-message',
        });
      }
      if (onRefreshData) {
        onRefreshData();
      }
    } catch (error) {
      console.error('Error al firmar exámenes:', error);
      message.error({
        content: `Error al firmar exámenes: ${error}`,
        style: { marginTop: '40vh' },
      });
    } finally {
      setSigningSelected(false);
      setSelectedRowKeys([]);
    }
  };

  const handleSelectRow = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: handleSelectRow,
    getCheckboxProps: (record) => ({
      disabled: record.expert_signed_at != null || !record.expertStatus,
    }),
  };

  const formatSessionTime = (sessionTime) => {
    if (!sessionTime) return '';
    const [days, hours, minutes] = sessionTime.split(',');
    if (days !== '00') return `${days} días`;
    return `${hours ? `${hours}h` : ''} ${minutes}m`;
  };

  const getTableColumns = (examType) => {
    const baseColumns = [
      {
        key: 'workerName',
        align: 'left',
        width: '15%',
        title: t('examPage.signatureTabs.patient'),
        render: (record) => {
          const isToday = record.admission_date
            ? moment().isSame(record.admission_date, 'day')
            : true;
          const isDisabled = !isToday || !!loadingPerRow[record.admission_patient_exam];
          return (
            <>
              <p className="mb-0">
                {record.full_name}
                {record.attendedInPerson && <Tag className="attended-in-person-tag">Presencial</Tag>}
              </p>
              {countryCode === 'PE' && hasBoxes && (
                <HasAccess
                  roles={['super_usuario', 'equipo_medico', 'equipo_psicologia', 'doctor', 'branch_office_manager']}
                  permission="call-patient"
                >
                  <div style={{ display: 'inline-block', marginTop: 5 }}>
                    <Popover
                      content={!isToday ? t('examPage.examTabs.callPatientDisabledTooltip') : ''}
                      placement="top"
                    >
                      <span>
                        <Button
                          loading={loadingPerRow[record.admission_patient_exam]}
                          disabled={isDisabled}
                          onClick={() => handleCallPatient(record.admission_patient_id, record.admission_patient_exam)}
                          className="cmt-button secondary mt-5 call-patient-button"
                          type="primary"
                          size="small"
                          style={isDisabled ? { pointerEvents: 'none' } : {}}
                        >
                          {t('examPage.examTabs.callPatient')}
                        </Button>
                      </span>
                    </Popover>
                  </div>
                </HasAccess>
              )}
            </>
          );
        },
      },
      {
        key: 'documentNumber',
        align: 'center',
        width: '12%',
        title: 'Identificador',
        render: (record) => {
          const formattedDocument = record.document_type === 'rut'
            ? handlers.formatDocument(record.document_number)
            : record.document_number;
          return <p className="mb-0">{formattedDocument}</p>;
        },
      },
      {
        key: 'branchOffice',
        align: 'center',
        width: '13%',
        title: 'Sede',
        render: (record) => (
          <p className="mb-0">{record.branchOfficeName || '-'}</p>
        ),
      },
      {
        key: 'examDetail',
        align: 'center',
        title: 'Detalle de examen',
        render: (record) => (
          <Tag
            className="exam-detail-tag"
            onClick={() => onOpenDetailDrawer && onOpenDetailDrawer(record)}
          >
            {record.specialtyName || 'Otros'}
          </Tag>
        ),
      },
      {
        key: 'viewDocument',
        align: 'center',
        width: '10%',
        title: 'Ver documento',
        render: (record) => {
          const hasDocument = record.viewableFiles?.length > 0 || record.dicomFiles?.length > 0 || !!record.documentResultId;
          return (
            <span
              className={`view-image-badge ${(!hasDocument || isSpecialtyUltrasound(record?.examSpecialtyId))? 'disabled' : ''}`}
              onClick={() => hasDocument && !isSpecialtyUltrasound(record?.examSpecialtyId) && onOpenDocumentDrawer && onOpenDocumentDrawer(record)}
            >
              <EyeOutlined />
            </span>
          );
        },
      },
      {
        key: 'examStatus',
        align: 'center',
        width: '10%',
        title: 'Estado',
        render: (record) => {
          const expertStatus = record.expertStatus;
          if (expertStatus?.toLowerCase() === 'normal') {
            return <span className="exam-status-badge status-normal">{capitalize(expertStatus)}</span>;
          }
          if (expertStatus?.toLowerCase() === 'alterado') {
            return <span className="exam-status-badge status-altered">{capitalize(expertStatus)}</span>;
          }
          if (expertStatus?.toLowerCase() === 'no realizado') {
            return <span className="exam-status-badge status-not-done">{capitalize(expertStatus)}</span>;
          }
          return <span className="exam-status-badge status-empty">Sin estado</span>;
        },
      },
    ];

    if (examType === 'notSignedServices' || examType === 'reevaluationServices') {
      baseColumns.push({
        key: 'sessionTime',
        align: 'center',
        width: '13%',
        title: 'Tiempo en sistema',
        render: (record) => (
          <p className="mb-0">{formatSessionTime(record.session_time)}</p>
        ),
      });
    }

    return baseColumns;
  };

  const globalCounts = {
    totalAttentions: signatureServices.notSignedServices?.counts?.totalAttentions
      ?? signatureServices.reevaluationServices?.counts?.totalAttentions
      ?? signatureServices.signedServices?.counts?.totalAttentions
      ?? 0,
    totalSigned: signatureServices.notSignedServices?.counts?.totalSigned
      ?? signatureServices.reevaluationServices?.counts?.totalSigned
      ?? signatureServices.signedServices?.counts?.totalSigned
      ?? 0,
    totalSignable: signatureServices.notSignedServices?.counts?.totalSignable
      ?? signatureServices.reevaluationServices?.counts?.totalSignable
      ?? signatureServices.signedServices?.counts?.totalSignable
      ?? 0,
  };

  const renderTableControlsBar = (examType, counts) => {
    const normalCount = counts?.normal ?? 0;
    const pendingCount = counts?.pending ?? 0;

    const showSignButton = examType === 'notSignedServices' || examType === 'reevaluationServices';

    return (
      <div className="signature-table-controls">
        <div className="signature-table-controls__left">
          {showSignButton && (
            <Button
              className="sign-selected-button"
              type="primary"
              onClick={handleSignSelected}
              disabled={selectedRowKeys.length === 0}
              loading={signingSelected}
            >
              Firmar seleccionados
            </Button>
          )}
        </div>
        <div className="signature-table-controls__right">
          <div className="total-attentions-pill">
            Total de atenciones: {globalCounts.totalAttentions}
          </div>
          <div className={`classification-toggle ${expertStatusFilter === 'pending' ? 'pending-active' : 'normal-active'}`}>
            <p className={`classification-toggle__label ${expertStatusFilter === 'normal' ? 'active' : ''}`}>
              Normal
            </p>
            <span className={`classification-toggle__badge ${expertStatusFilter === 'normal' ? 'normal' : 'inactive'}`}>
              {normalCount}
            </span>
            <Switch
              size="small"
              checked={expertStatusFilter === 'pending'}
              onChange={handleToggleClassification}
              className="classification-switch"
            />
            <p className="classification-toggle__label">
              Sin estado y alterados
            </p>
            <span className={`classification-toggle__badge ${expertStatusFilter === 'pending' ? 'altered' : 'inactive'}`}>
              {pendingCount}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Tabs
      className="cmt-tabs mt-30"
      defaultActiveKey="notSignedServices"
      tabBarGutter={60}
      onChange={(activeKey) => {
        setSelectedRowKeys([]);
        if (onChangeActiveTab) {
          onChangeActiveTab(activeKey);
        }
      }}
    >
      {['notSignedServices', 'reevaluationServices', 'signedServices'].map(examType => {
        const { data, pagination, loadingServices, currentPage, counts } = signatureServices[examType];
        const isSignableTab = examType === 'notSignedServices' || examType === 'reevaluationServices';

        return (
          <TabPane
            key={examType}
            tab={
              <Badge
                showZero
                offset={[15, 0]}
                count={(counts?.normal ?? 0) + (counts?.pending ?? 0)}
              >
                <p className="mb-0">
                  {examType === 'notSignedServices'
                    ? 'No firmados'
                    : examType === 'reevaluationServices'
                    ? 'Reevaluaciones'
                    : 'Firmados'}
                </p>
              </Badge>
            }
          >
            <Row justify="center">
              <Col lg={22} xs={22}>
                <Spin className="cmt-spin" spinning={loadingServices}>
                  {renderTableControlsBar(examType, counts)}

                  <Row gutter={[10, 10]}>
                    <Col lg={24} xs={24}>
                      <Table
                        className="cmt-table"
                        rowSelection={isSignableTab ? rowSelection : undefined}
                        columns={getTableColumns(examType)}
                        dataSource={data.map((exam, index) => ({
                          ...exam,
                          key: exam.admission_patient_exam || index,
                        }))}
                        pagination={false}
                        locale={{
                          emptyText: examType === 'notSignedServices'
                            ? 'No hay exámenes sin firmar'
                            : examType === 'reevaluationServices'
                            ? 'No hay exámenes de reevaluación'
                            : 'No hay exámenes firmados',
                        }}
                      />
                    </Col>

                    <Col lg={24} xs={24}>
                      <Row className="mt-30 mb-30" align="middle">
                        <Col>
                          <Radio.Group className="cmt-pagination">
                            <Radio.Button
                              value="prev"
                              disabled={currentPage === 1}
                              onClick={(event) => loadMore(event, examType)}
                            >
                              Anterior
                            </Radio.Button>
                            <Radio.Button
                              value="next"
                              disabled={pagination.lastPage === 0 || currentPage === pagination.lastPage}
                              onClick={(event) => loadMore(event, examType)}
                            >
                              Siguiente
                            </Radio.Button>
                          </Radio.Group>
                        </Col>
                        <Col>
                          <p className="ml-10 mt-5 mb-0">
                            {pagination.total ? pagination.from + 1 : 0} - {pagination.to} de {pagination.total}
                          </p>
                        </Col>
                        <Col flex="auto" className="text-right">
                          <p className="signed-exams-indicator">
                            Total exámenes firmados {globalCounts.totalSigned} de {globalCounts.totalAttentions}
                          </p>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Spin>
              </Col>
            </Row>
          </TabPane>
        );
      })}
    </Tabs>
  );
};

export default SignatureTabs;